import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { get, post, put, del, upload, fileUrl } from "@/lib/api";
import { PageLoader, PageHeader, EmptyState } from "@/components/common";
import { ChevronRight, ChevronDown, Plus, Trash2, Layers, ImagePlus } from "lucide-react";

function flatten(nodes, depth, open, out) {
  for (const n of nodes) {
    out.push({ node: n, depth });
    if (open[n.id] && n.children && n.children.length) flatten(n.children, depth + 1, open, out);
  }
  return out;
}

export default function CourseTaxonomy() {
  const qc = useQueryClient();
  const [open, setOpen] = useState({});
  const [adding, setAdding] = useState(null);
  const [name, setName] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["course-tax-tree"], queryFn: () => get("/course-taxonomy/tree") });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["course-tax-tree"] });
  const onAdd = async (parent_id) => {
    if (!name.trim()) return;
    await post("/course-taxonomy", { name, parent_id });
    setName(""); setAdding(null); toast.success("Added");
    if (parent_id) setOpen((o) => ({ ...o, [parent_id]: true }));
    invalidate();
  };
  const onRemove = async (id) => {
    try { await del(`/course-taxonomy/${id}`); toast.success("Deleted"); invalidate(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete children first"); }
  };
  const onLogo = async (node, file) => {
    try {
      const r = await upload(file);
      await put(`/course-taxonomy/${node.id}`, { name: node.name, parent_id: node.parent_id || null, logo: `/api/files/${r.id}`, description: node.description || "" });
      toast.success("Logo updated"); invalidate();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
  };

  const rows = data ? flatten(data, 0, open, []) : [];

  return (
    <div>
      <PageHeader title="Course Taxonomy" subtitle="Category → Sub-category → Inner sub-category → … (unlimited depth)" action={<button onClick={() => { setAdding({ parent_id: null }); setName(""); }} data-testid="add-course-category-btn" className="bg-indigo-800 text-white font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2"><Plus className="h-4 w-4" /> Add Category</button>} />
      {adding && adding.parent_id === null && (
        <div className="flex gap-2 mb-4 bg-white p-3 rounded-xl border border-slate-200">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onAdd(null)} data-testid="course-tax-name-input" className="border border-slate-200 rounded-lg px-3 py-2 text-sm flex-1" placeholder="New course category (e.g. Railways, Banking)" />
          <button onClick={() => onAdd(null)} className="bg-indigo-800 text-white px-4 rounded-lg text-sm font-semibold">Add</button>
          <button onClick={() => setAdding(null)} className="text-slate-500 px-3 text-sm">Cancel</button>
        </div>
      )}
      {isLoading ? <PageLoader /> : !data || data.length === 0 ? <EmptyState icon={Layers} title="No course categories yet" hint="Add a primary category to begin." /> : (
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          {rows.map(({ node, depth }) => {
            const hasChildren = node.children && node.children.length > 0;
            const isPrimary = depth === 0;
            return (
              <div key={node.id}>
                <div className="flex items-center gap-2 py-1.5 group" style={{ paddingLeft: depth * 18 }}>
                  {hasChildren ? (
                    <button onClick={() => setOpen({ ...open, [node.id]: !open[node.id] })} data-testid={`ct-toggle-${node.id}`}>
                      {open[node.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  ) : <span className="w-4 inline-block" />}
                  {isPrimary && (node.logo
                    ? <img src={fileUrl(node.logo.replace("/api", ""))} alt="" className="h-6 w-6 rounded object-cover border border-slate-200" data-testid={`ct-logo-${node.id}`} />
                    : <span className="h-6 w-6 rounded bg-slate-100 inline-flex items-center justify-center text-slate-300"><ImagePlus className="h-3.5 w-3.5" /></span>)}
                  <span className="font-medium text-slate-800">{node.name}</span>
                  {isPrimary && (
                    <label className="text-indigo-600 text-xs flex items-center gap-0.5 cursor-pointer" data-testid={`ct-logo-upload-${node.id}`}>
                      <ImagePlus className="h-3 w-3" /> logo
                      <input type="file" accept="image/*,.ico,.svg" className="hidden" onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; if (f) onLogo(node, f); }} />
                    </label>
                  )}
                  <button onClick={() => { setAdding({ parent_id: node.id }); setName(""); }} data-testid={`ct-add-child-${node.id}`} className="opacity-0 group-hover:opacity-100 text-indigo-600 text-xs flex items-center gap-0.5">
                    <Plus className="h-3 w-3" /> sub
                  </button>
                  <button onClick={() => onRemove(node.id)} className="opacity-0 group-hover:opacity-100 text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
                {adding && adding.parent_id === node.id && (
                  <div className="flex gap-2 my-1" style={{ paddingLeft: (depth + 1) * 18 }}>
                    <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && onAdd(node.id)} data-testid="course-tax-name-input" className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm" placeholder="New sub-category" />
                    <button onClick={() => onAdd(node.id)} className="bg-indigo-800 text-white px-3 rounded-lg text-sm">Add</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
